import { useTranslation } from 'next-i18next';
import type { FieldErrorsImpl, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';
import { FieldContainer } from '@storefront/lib-global/baseComponents/containers/FieldContainer';
import { ErrorMessage } from '@storefront/lib-global/baseComponents/errors';
import { Fieldset } from '@storefront/lib-global/baseComponents/inputs/Fieldset';
import { InputField, TextAreaWithCharacterCount } from '@storefront/lib-global/baseComponents/inputs/Input';
import type { GiftCardFormValues } from './giftCardInfo/GiftCardInfo';

const S = {
  GiftCardForm: styled.form`
    padding: 0 1px;

    fieldset > div {
      margin-bottom: 20px;
    }
  `,
};

export type GiftCardMessageFormProps = {
  isEGiftCard: boolean;
  register: UseFormRegister<GiftCardFormValues>;
  formError: Partial<FieldErrorsImpl<GiftCardFormValues>>;
  message?: string;
  to?: string;
  from?: string;
  email?: string;
};

export function GiftCardMessageForm({
  isEGiftCard,
  formError,
  register,
  message,
  email,
  from,
  to,
}: GiftCardMessageFormProps) {
  const { t } = useTranslation(['lib-products-common']);

  return (
    <S.GiftCardForm noValidate>
      <Fieldset label={t('pdp.giftcard.message.details')}>
        <FieldContainer>
          <InputField
            aria-label={t('pdp.giftcard.label.to')}
            placeholder={`${t('pdp.giftcard.label.to')}`}
            type="text"
            aria-describedby="to-invalid"
            aria-invalid={!!formError?.to}
            register={register('to')}
            isInputError={!!formError?.to}
            testId="giftCardTo"
            autoComplete="name"
            isEmpty={!to}
          />
          <ErrorMessage id="to-invalid" {...formError?.to}>
            {t(formError?.to?.message as string)}
          </ErrorMessage>
        </FieldContainer>
        {isEGiftCard && (
          <FieldContainer>
            <InputField
              aria-label={t('pdp.giftcard.label.email')}
              placeholder={`${t('pdp.giftcard.label.email')}*`}
              type="email"
              aria-describedby="email-invalid"
              aria-invalid={!!formError?.email}
              register={register('email')}
              isInputError={!!formError?.email}
              testId="giftCardRecipientEmail"
              autoComplete="email"
              required
              maxLength={128}
              isEmpty={!email}
            />
            <ErrorMessage id="email-invalid" {...formError?.to}>
              {t(formError?.email?.message as string)}
            </ErrorMessage>
          </FieldContainer>
        )}
        <FieldContainer>
          <InputField
            aria-label={t('pdp.giftcard.label.from')}
            placeholder={`${t('pdp.giftcard.label.from')}`}
            type="text"
            aria-describedby="from-invalid"
            aria-invalid={!!formError?.from}
            register={register('from')}
            isInputError={!!formError?.from}
            testId="giftCardFrom"
            autoComplete="name"
            isEmpty={!from}
          />
          <ErrorMessage id="from-invalid" {...formError?.from}>
            {t(formError?.from?.message as string)}
          </ErrorMessage>
        </FieldContainer>
        <FieldContainer>
          <TextAreaWithCharacterCount
            aria-label={t('pdp.giftcard.label.message')}
            register={register('message')}
            placeholder={`${t('pdp.giftcard.label.message')}`}
            characterLimit={180}
            message={message}
            testId="giftCardMessage"
            isEmpty={!message}
          />
        </FieldContainer>
      </Fieldset>
    </S.GiftCardForm>
  );
}
